<template>
  <ion-page>
    <ion-content class="h-full">
      <round-header image-type="component" :image="Cog" title="Settings" disable-edit-mode="true">
      </round-header>
      <div id="container">
        <div class="flex flex-wrap justify-center pt-2">
          <div
            v-for="entry in settingsMenu"
            :key="entry.label"
            class="mb-4 flex w-full justify-center"
          >
            <div
              class="font-fredoka font-outline-1 cursor-pointer rounded-xl bg-gray-500 px-4 py-2 text-xl text-white transition-all duration-200 hover:bg-gray-400 active:bg-gray-600"
              @click="entry.onClick()"
            >
              {{ entry.label }}
            </div>
          </div>
        </div>
      </div>
    </ion-content>

    <ion-modal :is-open="showEmojiModal" @did-dismiss="closeEmojiModal">
      <ion-content class="ion-padding">
        <div class="flex flex-col gap-4 p-4">
          <h2 class="font-fredoka text-2xl text-white">Request an Emoji</h2>
          <div class="flex flex-col gap-1">
            <label class="font-fredoka text-sm text-gray-300">Emoji Name</label>
            <input
              v-model="emojiName"
              placeholder="e.g. party_popper"
              class="font-fredoka rounded-xl bg-gray-700 px-4 py-2 text-white placeholder-gray-500 outline-none"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="font-fredoka text-sm text-gray-300">Description</label>
            <textarea
              v-model="emojiDescription"
              placeholder="Describe the emoji you'd like..."
              rows="4"
              class="font-fredoka rounded-xl bg-gray-700 px-4 py-2 text-white placeholder-gray-500 outline-none"
            />
          </div>
          <div class="flex gap-3 pt-2">
            <div
              class="font-fredoka font-outline-1 flex-1 cursor-pointer rounded-xl bg-gray-600 px-4 py-2 text-center text-xl text-white transition-all duration-200 hover:bg-gray-500 active:bg-gray-700"
              @click="closeEmojiModal"
            >
              Cancel
            </div>
            <div
              class="font-fredoka font-outline-1 flex-1 cursor-pointer rounded-xl px-4 py-2 text-center text-xl text-white transition-all duration-200"
              :class="
                isSubmitting
                  ? 'bg-gray-500'
                  : 'bg-indigo-500 hover:bg-indigo-400 active:bg-indigo-600'
              "
              @click="submitEmojiRequest"
            >
              {{ isSubmitting ? 'Sending...' : 'Submit' }}
            </div>
          </div>
        </div>
      </ion-content>
    </ion-modal>
  </ion-page>
</template>
<script setup lang="ts">
import { ref } from 'vue';
import { Cog } from 'lucide-vue-next';
import { IonContent, IonPage, IonModal } from '@ionic/vue';
import { Dialog } from '@capacitor/dialog';
import RoundHeader from '../components/RoundHeader.vue';
import { getFiles, API_URL } from '../services/Directus.ts';
import { userId } from '../services/FirebaseDB.ts';
import { CacheService } from '../services/CacheService.ts';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { PreferencesDB } from '../services/PreferencesDB.ts';
import { Profile } from '../models/Profile.ts';
import { Category } from '../models/Category.ts';
import { Task } from '../models/Task.ts';
import { Mood } from '../models/Mood.ts';
import { Gratitude } from '../models/Gratitude.ts';
import { Headache } from '../models/Headache.ts';

const showEmojiModal = ref(false);
const emojiName = ref('');
const emojiDescription = ref('');
const isSubmitting = ref(false);

const settingsMenu = [
  {
    label: 'Request Emoji',
    onClick: openEmojiModal,
  },
  {
    label: 'Export Data',
    onClick: exportData,
  },
  {
    label: 'Refresh Image Database',
    onClick: refreshImages,
  },
  {
    label: 'Delete Local Image Cache',
    onClick: clearImageCache,
  },
];

function openEmojiModal() {
  emojiName.value = '';
  emojiDescription.value = '';
  showEmojiModal.value = true;
}

function closeEmojiModal() {
  showEmojiModal.value = false;
}

async function submitEmojiRequest() {
  if (!emojiName.value.trim() || !emojiDescription.value.trim()) {
    await Dialog.alert({
      title: 'Missing fields',
      message: 'Please fill in both the name and description.',
    });
    return;
  }

  isSubmitting.value = true;
  try {
    const res = await fetch(`${API_URL}/items/emoji_requests`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        firebase_uid: userId.value,
        name: emojiName.value.trim(),
        description: emojiDescription.value.trim(),
      }),
    });

    if (!res.ok) throw new Error('Request failed');

    closeEmojiModal();
    await Dialog.alert({
      title: 'Request sent!',
      message: 'Your emoji request has been submitted. Thanks!',
    });
  } catch {
    await Dialog.alert({
      title: 'Error',
      message: 'Failed to submit your request. Please try again.',
    });
  } finally {
    isSubmitting.value = false;
  }
}

async function exportData() {
  const [profiles, categories, tasks, moods, gratitudes, headaches] = await Promise.all([
    PreferencesDB.getAll(Profile),
    PreferencesDB.getAll(Category),
    PreferencesDB.getAll(Task),
    PreferencesDB.getAll(Mood),
    PreferencesDB.getAll(Gratitude),
    PreferencesDB.getAll(Headache),
  ]);

  const exportPayload = {
    exportedAt: new Date().toISOString(),
    profile: profiles[0] ?? null,
    categories,
    tasks,
    moods,
    gratitudes,
    headaches,
  };

  const blob = new Blob([JSON.stringify(exportPayload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `retryli-export-${new Date().toISOString().slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

async function refreshImages() {
  const cache = new CacheService();
  await cache.remove('files');
  await getFiles(false);
}

async function clearImageCache() {
  try {
    await Filesystem.rmdir({
      path: 'images',
      directory: Directory.Cache,
      recursive: true,
    });

    await Filesystem.mkdir({
      directory: Directory.Cache,
      path: 'images',
    });
  } catch (error) {
    console.error('Fehler beim Löschen des Bildcaches:', error);
  }
}
</script>
